import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { toast } from "../utils/toast";
import { useSubmitGuard } from "../utils/useSubmitGuard";
import { errorMessage } from "../utils/errorMessage";
import {
  PLAN_NAMES,
  PLANS_PATH,
  PRICING_COPY,
  isMonthly,
  maxSavePercent,
  minimumTermLine,
  monthlyEquivalent,
  periodLabel,
  tierPlans,
} from "../utils/pricing";
import { API_DETAILS, COOKIE_KEYS } from "../App";
import "../styles/PricingPage.css";
import "../styles/Home.css";
import BrandLogo from "./BrandLogo";
import BillingToggle from "./BillingToggle";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * What a plan includes, from its row. Each item is [text, detail?]. Every
 * number comes from the plan; only the wording is here.
 */
const featuresFor = (plan) => {
  const features = [];
  if (plan.diagnosisLimit != null) {
    features.push([plan.diagnosisLimit > 1
      ? `Up to ${plan.diagnosisLimit} diagnoses treated`
      : plural(plan.diagnosisLimit, "diagnosis treated", "diagnoses treated")]);
  }
  if (plan.numAppointments != null) {
    if (!plan.recurring) {
      features.push([`${plural(plan.numAppointments, "video visit", "video visits")} with your urology-trained provider`]);
    } else if (isMonthly(plan) && plan.numAppointments >= 4 && plan.numAppointments % 4 === 0) {
      // Monthly members are given a quarter of the year's visits every three
      // months (VisitAllowance), so the year's total alone would oversell month one.
      features.push([`${plan.numAppointments} video visits a year with your urology-trained provider`,
        `${plural(plan.numAppointments / 4, "visit", "visits")} every 3 months`]);
    } else {
      features.push([`${plan.numAppointments} video visits a year with your urology-trained provider`]);
    }
  }
  if (plan.recurring) {
    features.push(["Symptom Score before every visit"]);
  }
  features.push([
    plan.chatDurationDays && !plan.recurring
      ? `Direct messaging with your provider for ${plural(plan.chatDurationDays, "day", "days")}`
      : "Direct messaging with your provider",
    `Replies within ${PRICING_COPY.messageResponseBusinessDays} business days`,
  ]);
  if (plan.recurring) {
    features.push(["Prescription refills"]);
  }
  if (plan.educationAccess) {
    features.push(["Curated educational content"]);
  }
  return features;
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [apiPlans, setApiPlans] = useState([]);
  // "loading" until the plans arrive. The cards have no prices of their own,
  // so there is nothing honest to show before then.
  const [plansState, setPlansState] = useState("loading");
  const [billing, setBilling] = useState("annual");
  // The plan whose checkout is opening; it stays set through the redirect to
  // Stripe so every button stays disabled. The guard is the lock against two
  // clicks in the same frame.
  const [busyPlanId, setBusyPlanId] = useState(null);
  const [, guard] = useSubmitGuard();

  // Plan ids live in the database alongside the Stripe billing details. Reading
  // them here is what lets a returning patient go straight from this page to
  // payment.
  useEffect(() => {
    axios
      .get(API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + PLANS_PATH)
      .then((res) => {
        setApiPlans(res.data || []);
        setPlansState("loaded");
      })
      .catch(() => {
        setApiPlans([]);
        setPlansState("failed");
      });
  }, []);

  // An existing patient whose membership lapsed already has an account, so the
  // sign-up flow is a dead end for them. Matching userType too keeps providers
  // and admins on the sign-up path.
  const userId = Cookies.get(COOKIE_KEYS.userId);
  const hasExistingSession = Boolean(userId) && Cookies.get(COOKIE_KEYS.userType) === "PATIENT";

  const one = tierPlans(apiPlans, "STANDARD");
  const plus = tierPlans(apiPlans, "PREMIUM");
  const onDemand = apiPlans.find((p) => p.name === "ONETIME") || null;
  const hasMonthly = Boolean(one.monthly || plus.monthly);
  // Without monthly plans there is nothing to toggle between.
  const showing = hasMonthly ? billing : "annual";
  const pick = (tier) => (showing === "monthly" ? tier.monthly : tier.annual);
  const savePercent = maxSavePercent(apiPlans);
  const monthlyMinimum = minimumTermLine(one.monthly || plus.monthly);

  const memberships = [
    { plan: pick(one), primary: true, badge: "Most popular" },
    { plan: pick(plus), primary: false, badge: "" },
  ].filter((m) => m.plan);

  const busy = busyPlanId != null;

  /**
   * A signed-in patient goes straight from here to Stripe. Someone without an
   * account registers first, since a checkout has to be attached to a patient.
   */
  const choose = guard(async (plan) => {
    if (busy) return;

    if (!hasExistingSession) {
      navigate("/signup", { state: { selectedPlan: PLAN_NAMES[plan.name], planId: plan.planId } });
      return;
    }

    setBusyPlanId(plan.planId);
    try {
      const res = await axios.post(
        API_DETAILS.baseUrl +
          API_DETAILS.PORT +
          API_DETAILS.baseExtension +
          `/payment/create-checkout-session?userId=${encodeURIComponent(userId)}&planId=${encodeURIComponent(plan.planId)}`
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("We couldn't open the payment page. Try again in a moment.");
        setBusyPlanId(null);
      }
    } catch (err) {
      // The server refuses plans the patient isn't eligible for, for instance a
      // one-time pass while their membership is still running, and its message
      // explains why.
      toast.error(errorMessage(err, "We couldn't start checkout. Try again in a moment."));
      setBusyPlanId(null);
    }
  });

  const ctaLabel = (plan) =>
    busyPlanId === plan.planId
      ? "Opening secure payment…"
      : hasExistingSession
      ? "Continue to Payment"
      : "Continue to Sign Up";

  const Features = ({ plan }) => (
    <ul className="plan-features">
      {featuresFor(plan).map(([text, detail]) => (
        <li key={text} className="feature">
          <span className="check" aria-hidden="true">✓</span>
          <span>
            {text}
            {detail ? <span className="feature-detail">{detail}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="pricing-page">
      <header className="home-nav">
        <Link to="/" className="home-nav-left" aria-label="Youro home">
          <BrandLogo />
        </Link>
        <nav className="home-nav-center">
          <button className="home-nav-link" onClick={() => navigate("/about")}>
            About
          </button>
          <button className="home-nav-link" onClick={() => navigate("/conditions")}>
            Find Your Diagnosis
          </button>
          <button className="home-nav-link" onClick={() => navigate("/assessment")}>
            Symptom Assessment
          </button>
        </nav>
        <div className="home-nav-right">
          <button className="home-nav-signin" onClick={() => navigate("/login")}>
            Sign In
          </button>
          <button className="home-nav-get-started" onClick={() => navigate("/signup")}>
            Get Started
          </button>
        </div>
      </header>

      <main className="pricing-content">
        <button className="pricing-back" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <h1 className="pricing-title">Simple pricing. Real urology care.</h1>

        <p className="pricing-subtitle">
          One flat membership covers your visits. Prescriptions and labs go through your own insurance or pharmacy.
        </p>

        {plansState === "loading" && (
          <p className="pricing-status" role="status">Loading plans…</p>
        )}
        {plansState !== "loading" && memberships.length === 0 && !onDemand && (
          <p className="pricing-status" role="alert">
            We couldn't load our plans just now. Refresh the page to try again.
          </p>
        )}

        {hasMonthly && (
          <div className="pricing-toggle-row">
            <BillingToggle value={showing} onChange={setBilling} savePercent={savePercent} />
          </div>
        )}

        <div className="pricing-grid">
          {memberships.map(({ plan, primary, badge }) => (
            <section key={plan.planId} className="pricing-card" aria-labelledby={`plan-${plan.planId}`}>
              {badge ? <span className="pricing-badge">{badge}</span> : null}
              <p className="plan-name" role="heading" aria-level={2} id={`plan-${plan.planId}`}>
                {PLAN_NAMES[plan.name]}
              </p>

              {/* Keyed on the billing period so the fade runs on each switch. */}
              <div className="plan-price-block" key={showing}>
                <div className="plan-price">
                  <span className="price">${plan.price}</span>
                  <span className="period">{periodLabel(plan)}</span>
                </div>
                {isMonthly(plan) && !minimumTermLine(plan) ? null : (
                  <p className="plan-price-note">
                    {isMonthly(plan) ? minimumTermLine(plan) : `Equivalent to $${monthlyEquivalent(plan)}/mo`}
                  </p>
                )}
              </div>

              <Features plan={plan} />

              <button
                type="button"
                className={primary ? "plan-cta plan-cta-primary" : "plan-cta plan-cta-secondary"}
                disabled={busy}
                onClick={() => choose(plan)}
              >
                {ctaLabel(plan)}
              </button>
            </section>
          ))}

          {onDemand && (
            <section className="pricing-card pricing-card-secondary" aria-labelledby={`plan-${onDemand.planId}`}>
              <p className="plan-name" role="heading" aria-level={2} id={`plan-${onDemand.planId}`}>
                {PLAN_NAMES.ONETIME}
              </p>
              <div className="plan-price-block">
                <div className="plan-price">
                  <span className="price">${onDemand.price}</span>
                  <span className="period">one-time</span>
                </div>
                <p className="plan-price-note">
                  Upgrade to a membership within {PRICING_COPY.onDemandCreditWindowDays} days and your ${onDemand.price} counts
                  toward it.
                </p>
              </div>

              <Features plan={onDemand} />

              <button
                type="button"
                className="plan-cta plan-cta-secondary"
                disabled={busy}
                onClick={() => choose(onDemand)}
              >
                {ctaLabel(onDemand)}
              </button>
            </section>
          )}
        </div>

        {(memberships.length > 0 || onDemand) && (
          <p className="pricing-fine-print">
            Memberships renew automatically at the end of each billing period until canceled.
            {monthlyMinimum ? ` Monthly plans have a ${(one.monthly || plus.monthly).minimumTermMonths}-month minimum commitment.` : ""}{" "}
            Cancel anytime from Billing in your profile. Membership fees cover visits and provider messaging;
            medications and lab tests are billed separately by your pharmacy or lab.
            {onDemand
              ? ` On Demand fees are credited toward a membership purchased within ${PRICING_COPY.onDemandCreditWindowDays} days.`
              : ""}
          </p>
        )}
      </main>
    </div>
  );
};

export default PricingPage;
